import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, MapPin, Search, User, X } from "lucide-react";
import { isStaff, type Principal, type ShareTarget } from "@cd/core";
import { api, isGotabOperator } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { Input, Label, Select } from "../../components/ui/primitives";

const STAFF_ORG_ID = "gotab-staff";
/** People picker value meaning "other GoTab staff" rather than a location. */
const STAFF_DIRECTORY = "__staff__";

const key = (t: Pick<ShareTarget, "type" | "id">) => `${t.type}:${t.id}`;

/** Pick who can view a dashboard: the whole org, any of your locations, or specific people. */
export function ShareDialog({
  open, onOpenChange, dashboardId, title, shares, me,
}: { open: boolean; onOpenChange: (o: boolean) => void; dashboardId: string; title: string; shares: ShareTarget[]; me: Principal }) {
  const qc = useQueryClient();
  const [targets, setTargets] = useState<ShareTarget[]>(shares);
  const [dirLocation, setDirLocation] = useState("");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    if (open) setTargets(shares);
  }, [open, shares]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const locations = useQuery({ queryKey: ["locations"], queryFn: api.locations, enabled: open });
  useEffect(() => {
    if (!dirLocation && locations.data?.[0]) setDirLocation(String(locations.data[0].id));
  }, [dirLocation, locations.data]);

  const people = useQuery({
    queryKey: ["share-directory", dirLocation, debounced],
    queryFn: () => api.shareDirectory(debounced, dirLocation === STAFF_DIRECTORY ? undefined : dirLocation),
    enabled: open && Boolean(dirLocation),
  });

  const save = useMutation({
    mutationFn: () => api.setShares(dashboardId, targets),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard", dashboardId] });
      onOpenChange(false);
    },
  });

  const has = (t: Pick<ShareTarget, "type" | "id">) => targets.some((x) => key(x) === key(t));
  const toggle = (t: ShareTarget) => setTargets((cur) => (has(t) ? cur.filter((x) => key(x) !== key(t)) : [...cur, t]));
  const orgTarget: ShareTarget = { type: "org", id: me.orgId, label: me.orgId === STAFF_ORG_ID ? "All GoTab staff" : "Everyone in your organization" };
  const users = targets.filter((t) => t.type === "user");

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Share ${title}`} className="w-[520px] max-w-[calc(100vw-32px)] p-6">
      <h2 className="text-base font-semibold tracking-tight">Share “{title}”</h2>
      <p className="mt-1 text-sm text-muted">
        People you share with can view this dashboard and duplicate it. Each person only ever sees data for their own locations.
      </p>

      <div className="mt-5 space-y-5">
        {/* A GoTab operator's org is just themselves: share by location or person instead. */}
        {!isGotabOperator(me) && (
          <CheckRow checked={has(orgTarget)} onClick={() => toggle(orgTarget)} icon={<Building2 className="size-3.5 text-subtle" />}>
            {orgTarget.label}
          </CheckRow>
        )}

        <section>
          <Label>Locations</Label>
          <p className="mt-0.5 text-xs text-subtle">Everyone with access to the location.</p>
          <div className="mt-1.5 max-h-40 overflow-y-auto">
            {locations.data?.map((l) => {
              const t: ShareTarget = { type: "location", id: String(l.id), label: String(l.name) };
              return (
                <CheckRow key={t.id} checked={has(t)} onClick={() => toggle(t)} icon={<MapPin className="size-3.5 text-subtle" />}>
                  {t.label}
                </CheckRow>
              );
            })}
          </div>
        </section>

        <section>
          <Label>People</Label>
          <div className="mt-1.5 flex gap-2">
            <Select value={dirLocation} onChange={(e) => setDirLocation(e.target.value)} className="max-w-48" aria-label="People at">
              {locations.data?.map((l) => <option key={String(l.id)} value={String(l.id)}>{String(l.name)}</option>)}
              {isStaff(me) && <option value={STAFF_DIRECTORY}>GoTab staff</option>}
            </Select>
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="pl-8" />
            </div>
          </div>
          <div className="mt-1 max-h-40 overflow-y-auto">
            {people.data?.map((p) => {
              const t: ShareTarget = { type: "user", id: p.id, label: p.name };
              return (
                <CheckRow key={p.id} checked={has(t)} onClick={() => toggle(t)} icon={<User className="size-3.5 text-subtle" />}>
                  {p.name}
                  {p.email && <span className="ml-1.5 text-xs text-subtle">{p.email}</span>}
                </CheckRow>
              );
            })}
            {people.isSuccess && !people.data.length && <p className="px-2 py-2 text-sm text-subtle">No matching people.</p>}
            {people.error && <p className="px-2 py-2 text-sm text-bad">{people.error.message}</p>}
          </div>
          {users.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {users.map((u) => (
                <span key={u.id} className="inline-flex items-center gap-1 rounded-md bg-accent-soft px-2 py-1 text-xs text-accent">
                  {u.label ?? u.id}
                  <button onClick={() => toggle(u)} aria-label={`Stop sharing with ${u.label ?? u.id}`}><X className="size-3" /></button>
                </span>
              ))}
            </div>
          )}
        </section>
      </div>

      {save.error && <p className="mt-4 text-sm text-bad">{save.error.message}</p>}
      <div className="mt-6 flex items-center justify-between">
        <span className="text-xs text-subtle">{targets.length ? `Shared with ${targets.length}` : "Only you"}</span>
        <div className="flex gap-2">
          <Button onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </div>
      </div>
    </Dialog>
  );
}

function CheckRow({ checked, onClick, icon, children }: { checked: boolean; onClick: () => void; icon: ReactNode; children: ReactNode }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-surface-2">
      <span className={cn("flex size-4 shrink-0 items-center justify-center rounded border border-border-strong", checked && "border-accent bg-accent text-white")}>
        {checked && <Check className="size-3" />}
      </span>
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
}
